import React, { useState, useEffect, useMemo } from "react";
import { styled, keyframes, useTheme, Box } from "@mui/material";

const WhatWeCookToday = () => {
  const theme = useTheme();

  // Simplified text variants
  const textVariants = useMemo(
    () => [
      "What are we cooking today?",
      "What delicious dish shall we create?",
      "What culinary adventure awaits?",
      "What flavors shall we explore today?",
      "What amazing recipe will you try?",
    ],
    []
  );

  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  const fullText = textVariants[currentTextIndex];

  // Simple typing effect
  useEffect(() => {
    const typingSpeed = isDeleting ? 50 : 100;
    const pauseTime = 2000;

    const timer = setTimeout(() => {
      if (!isDeleting && charIndex < fullText.length) {
        // Typing forward
        setDisplayText(fullText.substring(0, charIndex + 1));
        setCharIndex(charIndex + 1);
      } else if (!isDeleting && charIndex === fullText.length) {
        // Finished typing, pause before deleting
        setTimeout(() => {
          setIsDeleting(true);
        }, pauseTime);
      } else if (isDeleting && charIndex > 0) {
        // Deleting backward
        setDisplayText(fullText.substring(0, charIndex - 1));
        setCharIndex(charIndex - 1);
      } else if (isDeleting && charIndex === 0) {
        // Finished deleting, move to next text
        setIsDeleting(false);
        setCurrentTextIndex((prev) => (prev + 1) % textVariants.length);
      }
    }, typingSpeed);

    return () => clearTimeout(timer);
  }, [charIndex, isDeleting, fullText, textVariants.length]);

  return (
    <Container>
      <Title theme={theme}>
        {displayText}
        <Cursor theme={theme} />
      </Title>
    </Container>
  );
};

// Simple animations
const cursorBlink = keyframes`
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Simplified styled components
const Container = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "auto",
  minHeight: "200px",
  padding: "2rem 1rem",
  animation: `${fadeIn} 1s ease-out`,

  // Responsive adjustments
  "@media (max-width: 768px)": {
    minHeight: "150px",
    padding: "1.5rem 0.5rem",
  },

  "@media (max-width: 480px)": {
    minHeight: "120px",
    padding: "1rem 0.25rem",
  },
});

const Title = styled("h1")(({ theme }) => ({
  fontSize: "clamp(1.5rem, 4vw, 3.5rem)",
  fontWeight: 600,
  margin: 0,
  textAlign: "center",
  color: theme.palette.primary.main,
  fontFamily: '"Inter", "Roboto", "Arial", sans-serif',
  letterSpacing: "0.02em",
  lineHeight: 1.3,
  width: "100%",
  minHeight: "1.3em", // Prevent layout shift
  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  // Responsive text sizing
  "@media (max-width: 768px)": {
    fontSize: "clamp(1.25rem, 5vw, 2.5rem)",
    lineHeight: 1.4,
  },

  "@media (max-width: 480px)": {
    fontSize: "clamp(1rem, 6vw, 2rem)",
    lineHeight: 1.5,
  },

  // Smooth color transition in dark mode
  transition: "color 0.3s ease",

  // Better text rendering
  textRendering: "optimizeLegibility",
  WebkitFontSmoothing: "antialiased",
  MozOsxFontSmoothing: "grayscale",
}));

const Cursor = styled("span")(({ theme }) => ({
  display: "inline-block",
  width: "3px",
  height: "1em",
  backgroundColor: theme.palette.primary.main,
  marginLeft: "4px",
  animation: `${cursorBlink} 1s infinite`,
  borderRadius: "1px",

  // Responsive cursor size
  "@media (max-width: 768px)": {
    width: "2px",
    marginLeft: "3px",
  },

  "@media (max-width: 480px)": {
    width: "2px",
    marginLeft: "2px",
  },
}));

export default React.memo(WhatWeCookToday);
